/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import { useEstoqueFilter } from '../../../context/FiltersContext/EstoqueFilter'
import api from '../../../services/axios'
import { IoCloseCircleSharp } from 'react-icons/io5'
import { FaCheck, FaSearch } from 'react-icons/fa'
import { ItemContainerStyle } from '../GeralFilter/styles'
import { carregarDistribuidores } from '../../../services/dists'
import { useAuth } from '../../../context/AuthContext'

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

interface ModalProps {
  closeModal: () => void
}

interface Distributor {
  id?: string | number
  _id?: string | number
  name?: string
  fantasy_name?: string
  nome?: string
  razao_social?: string
  regional?: string
  operacao?: string
  gestor_id?: string | number
}

export default function EstoqueFilterBar({ closeModal }: ModalProps) {
  const { user } = useAuth()
  const [userRole, setUserRole] = useState<string | null>(null)
  const [userMarca, setUserMarca] = useState('')

  const {
    year,
    setYear,
    month,
    setMonth,
    distributorIds,
    toggleDistributor,
    selectAllDistributors,
    clearDistributors,
    regional,
    setRegional,
    gestor,
    setGestor,
    marca,
    setMarca,
    categoria,
    setCategoria,
    resetFilters,
  } = useEstoqueFilter()

  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [operacoesList, setOperacoesList] = useState<string[]>([])
  const [gestoresList, setGestoresList] = useState<any[]>([])
  const [marcasList, setMarcasList] = useState<string[]>([])
  const [categoriasList, setCategoriasList] = useState<string[]>([])
  const [distSearch, setDistSearch] = useState('')

  useEffect(() => {
    const initData = async () => {
      try {
        const role = user?.role ? String(user.role) : ''
        const vMarca = user?.vinc_marca ? String(user.vinc_marca) : ''
        setUserRole(role)
        setUserMarca(vMarca)

        let dists: any[] = []

        dists = await carregarDistribuidores()
        setDistributors(dists)

        const operacoesUnicas = Array.from(
          new Set(dists.map((d: any) => d.regional || d.operacao || d.regiao).filter(Boolean)),
        ) as string[]
        setOperacoesList(operacoesUnicas)

        const [prodRes, getProdRes] = await Promise.all([
          api.get('/int/produtos', { params: { offset: 0, limit: 10000 } }).catch(() => ({ data: [] })),
          api.get('/get/produtos', { params: { offset: 0, limit: 10000 } }).catch(() => ({ data: [] })),
        ])
        const intProds = prodRes.data?.data || prodRes.data || []
        const getProds = getProdRes.data?.data || getProdRes.data || []
        const produtos = [
          ...(Array.isArray(intProds) ? intProds : []),
          ...(Array.isArray(getProds) ? getProds : []),
        ]

        const marcasUnicas = Array.from(
          new Set(produtos.map((p: any) => p.marca || p.vinc_marca || p.ds_marca).filter(Boolean)),
        ) as string[]

        const categoriasUnicas = Array.from(
          new Set(
            produtos
              .map((p: any) => p.categoria || p.familia || p.ds_categoria || p.ds_familia)
              .filter(Boolean),
          ),
        ) as string[]

        const marcasPermitidas = vMarca
          ? vMarca.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
          : []

        const marcasFiltradas = marcasPermitidas.length > 0
          ? marcasUnicas.filter((m) => marcasPermitidas.includes(m.toUpperCase()))
          : marcasUnicas

        if (marcasPermitidas.length > 0) {
          setMarcasList(marcasFiltradas.length > 0 ? marcasFiltradas : marcasPermitidas)
          if (marcasFiltradas.length === 1) {
            setMarca(marcasFiltradas[0])
          }
        } else {
          setMarcasList(marcasUnicas)
        }
        setCategoriasList(categoriasUnicas)

        const usersRes = await api.get('/users')
        const usersList = usersRes.data?.data || usersRes.data || []
        const apenasGestores = usersList.filter(
          (u: any) => (u.role || '').toString().toLowerCase() === 'gestor',
        )
        setGestoresList(apenasGestores)
      } catch (error) {
        console.error('Erro ao carregar opções de filtro:', error)
      }
    }
    initData()
  }, [setMarca, user])

  const years = useMemo(() => {
    const current = new Date().getFullYear()
    return Array.from({ length: 9 }, (_, i) => current - i)
  }, [])

  // Filtrar distribuidores exibidos na listagem com base na busca, regional e gestor
  const filteredDistributorsList = useMemo(() => {
    return distributors.filter((d: any) => {
      const name = d.fantasy_name || d.name || d.nome || d.razao_social || `Distribuidor ${d.id}`
      const matchesSearch = name.toLowerCase().includes(distSearch.toLowerCase())

      const reg = (d.regional || d.operacao || d.regiao || '').trim().toLowerCase()
      const matchesRegional =
        !regional || reg === regional.trim().toLowerCase()

      const matchesGestor =
        !gestor || (d.gestor_id && String(d.gestor_id) === String(gestor))

      return matchesSearch && matchesRegional && matchesGestor
    })
  }, [distributors, distSearch, regional, gestor])

  const handleSelectAllVisible = () => {
    const visibleIds = filteredDistributorsList.map((d: any) => d.id || d._id).filter(Boolean)
    selectAllDistributors(visibleIds)
  }

  const handleRegionalChange = (val: string) => {
    const newVal = val || null
    setRegional(newVal)
    if (newVal) {
      const matching = distributors.filter((d: any) => {
        const reg = (d.regional || d.operacao || d.regiao || '').trim().toLowerCase()
        const matchesGestor = !gestor || (d.gestor_id && String(d.gestor_id) === String(gestor))
        return reg === newVal.trim().toLowerCase() && matchesGestor
      })
      const ids = matching.map((d: any) => d.id || d._id).filter(Boolean)
      selectAllDistributors(ids)
    } else {
      clearDistributors()
    }
  }

  const handleGestorChange = (val: string) => {
    const newGestor = val || null
    setGestor(newGestor)
    if (newGestor) {
      const matching = distributors.filter((d: any) => {
        const matchesGestor = d.gestor_id && String(d.gestor_id) === String(newGestor)
        const reg = (d.regional || d.operacao || d.regiao || '').trim().toLowerCase()
        const matchesReg = !regional || reg === regional.trim().toLowerCase()
        return matchesGestor && matchesReg
      })
      const ids = matching.map((d: any) => d.id || d._id).filter(Boolean)
      selectAllDistributors(ids)
    } else if (!regional) {
      clearDistributors()
    } else {
      const matching = distributors.filter((d: any) => {
        const reg = (d.regional || d.operacao || d.regiao || '').trim().toLowerCase()
        return reg === regional.trim().toLowerCase()
      })
      const ids = matching.map((d: any) => d.id || d._id).filter(Boolean)
      selectAllDistributors(ids)
    }
  }

  const selectStyle: React.CSSProperties = {
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    fontWeight: 'bold',
    width: '100%',
    fontFamily: 'Montserrat, sans-serif',
    backgroundColor: '#ffffff',
    color: '#000000',
    fontSize: '13px',
  }

  return (
    <>
      <style>{`
        select {
          color: #000000 !important;
          background-color: #ffffff !important;
        }
        select option {
          color: #000000 !important;
          background-color: #ffffff !important;
        }
        .dist-checkbox-item:hover {
          background-color: rgba(166, 193, 11, 0.15) !important;
        }
      `}</style>

      <div className='header' style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '14px', color: '#ffffff', letterSpacing: '1px', fontWeight: 'bold' }}>
          FILTROS DE ESTOQUE
        </h2>
        <button
          onClick={() => closeModal()}
          type='button'
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ffffff',
            fontSize: 24,
            cursor: 'pointer',
            position: 'absolute',
            right: 15,
            top: 13,
            display: 'flex',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <IoCloseCircleSharp />
        </button>
      </div>

      <div
        className='body no-print'
        style={{
          fontFamily: "'Montserrat', sans-serif",
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          height: 'calc(100vh - 50px)',
          overflowY: 'auto',
          padding: '20px',
          boxSizing: 'border-box',
        }}
      >
        {/* ANO E MÊS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              ANO
            </span>
            <select
              style={selectStyle}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </ItemContainerStyle>

          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              MÊS
            </span>
            <select
              style={selectStyle}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {monthNames.map((m, idx) => (
                <option key={idx} value={idx}>
                  {m.toUpperCase()}
                </option>
              ))}
            </select>
          </ItemContainerStyle>
        </div>

        {/* OPERAÇÃO / REGIONAL E GESTOR */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              OPERAÇÃO / REGIONAL
            </span>
            <select
              style={selectStyle}
              value={regional || ''}
              onChange={(e) => handleRegionalChange(e.target.value)}
            >
              <option value=''>Todos</option>
              {operacoesList.map((op, idx) => (
                <option key={idx} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </ItemContainerStyle>

          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              GESTOR
            </span>
            <select
              style={selectStyle}
              value={gestor || ''}
              onChange={(e) => handleGestorChange(e.target.value)}
            >
              <option value=''>Todos</option>
              {gestoresList.map((g: any) => {
                const gId = g.id || g._id
                const gName = g.fantasy_name || g.name || g.nome || g.username || g.email || `Gestor (${gId})`
                return (
                  <option key={gId} value={gId}>
                    {gName}
                  </option>
                )
              })}
            </select>
          </ItemContainerStyle>
        </div>

        {/* MARCAS GSA E CATEGORIA / FAMÍLIA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              MARCAS GSA
            </span>
            <select
              style={selectStyle}
              value={marca || ''}
              onChange={(e) => setMarca(e.target.value || null)}
              disabled={!!userMarca}
            >
              {!userMarca && <option value=''>Todos</option>}
              {marcasList.map((m, idx) => (
                <option key={idx} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </ItemContainerStyle>

          <ItemContainerStyle>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff', marginBottom: '4px' }}>
              CATEGORIA / FAMÍLIA
            </span>
            <select
              style={selectStyle}
              value={categoria || ''}
              onChange={(e) => setCategoria(e.target.value || null)}
            >
              <option value=''>Todos</option>
              {categoriasList.map((cat, idx) => (
                <option key={idx} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </ItemContainerStyle>
        </div>

        {/* MULTI-SELEÇÃO DE DISTRIBUIDORES */}
        <ItemContainerStyle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffffff' }}>
              DISTRIBUIDORES ({distributorIds.length === 0 ? 'TODOS' : `${distributorIds.length} selecionado(s)`})
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type='button'
                onClick={handleSelectAllVisible}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#a6c10b',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Todos
              </button>
              <span style={{ color: '#666', fontSize: '11px' }}>|</span>
              <button
                type='button'
                onClick={clearDistributors}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ff4d4f',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Limpar
              </button>
            </div>
          </div>

          {/* Busca de Distribuidor */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '6px 10px',
              marginBottom: '8px',
              border: '1px solid #ccc',
            }}
          >
            <FaSearch style={{ color: '#888', marginRight: '6px', fontSize: '12px' }} />
            <input
              type='text'
              placeholder='Buscar distribuidor...'
              value={distSearch}
              onChange={(e) => setDistSearch(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                fontSize: '12px',
                fontFamily: 'Montserrat, sans-serif',
              }}
            />
          </div>

          {/* Lista com Checkboxes */}
          <div
            style={{
              maxHeight: '160px',
              overflowY: 'auto',
              backgroundColor: 'rgba(0, 20, 60, 0.6)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '4px',
            }}
          >
            {filteredDistributorsList.length === 0 ? (
              <div style={{ padding: '10px', textAlign: 'center', color: '#8e9aaf', fontSize: '12px' }}>
                Nenhum distribuidor encontrado
              </div>
            ) : (
              filteredDistributorsList.map((d: any) => {
                const id = d.id || d._id
                const name = d.fantasy_name || d.name || d.nome || d.razao_social || `Distribuidor ${id}`
                const isSelected = distributorIds.some((selectedId) => String(selectedId) === String(id))

                return (
                  <div
                    key={id}
                    className='dist-checkbox-item'
                    onClick={() => toggleDistributor(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      backgroundColor: isSelected ? 'rgba(166, 193, 11, 0.2)' : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '4px',
                        border: isSelected ? '2px solid #a6c10b' : '1px solid #8e9aaf',
                        backgroundColor: isSelected ? '#a6c10b' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && <FaCheck size={10} color='#001D53' />}
                    </div>
                    <span
                      style={{
                        color: isSelected ? '#ffffff' : '#e0e0e0',
                        fontSize: '12px',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={name}
                    >
                      {name}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </ItemContainerStyle>

        {/* BOTÕES DE AÇÃO */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button
            type='button'
            onClick={resetFilters}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #8e9aaf',
              cursor: 'pointer',
              color: '#ffffff',
              backgroundColor: 'transparent',
              fontWeight: 'bold',
              fontSize: '12px',
            }}
          >
            REDEFINIR
          </button>
          <button
            type='button'
            onClick={() => closeModal()}
            style={{
              flex: 1.5,
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: '#a6c10b',
              color: '#001D53',
              fontWeight: 'bold',
              fontSize: '12px',
            }}
          >
            APLICAR FILTROS
          </button>
        </div>
      </div>
    </>
  )
}
